import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import {
  ExportDownloadPayload,
  ExportMode,
  Extension,
  UrlToFile,
} from '@/Domain/api/Export/export.dto';

const exportAnalyticsSchema = z
  .object({
    start_date: z.date(),
    end_date: z.date(),
    extension: z.nativeEnum(Extension),
    query: z.string().optional(),
    send_email_notification: z.boolean(),
  })
  .refine((data) => data.start_date <= data.end_date, {
    path: ['end_date'],
    message: 'End date must be greater than or equal to start date',
  });

type exportAnalyticsForm = z.infer<typeof exportAnalyticsSchema>;

const exportModeToMutationKey: Record<ExportMode, readonly unknown[]> = {
  [ExportMode.CONVERSATIONS]: [QueryKey.export.conversations],
  [ExportMode.DATA_GATHERING]: [QueryKey.export.dataGathering],
  [ExportMode.LINK_TRACKING]: [QueryKey.export.linkTracking],
  [ExportMode.AI_INSIGHTS]: [QueryKey.export.aiInsights],
  [ExportMode.TRIAGE_DATA]: [QueryKey.export.triageData],
};

export const useExportAnalyticsForm = (exportMode: ExportMode) => {
  const formatDateForApi = (date: Date): string => format(date, 'yyyy-MM-dd');

  const today = new Date();
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);

  const form = useForm<exportAnalyticsForm>({
    mode: 'onChange',
    defaultValues: {
      start_date: lastWeek,
      end_date: today,
      extension: Extension.CSV,
      send_email_notification: true,
    },
    resolver: zodResolver(exportAnalyticsSchema),
  });
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query') || undefined;

  const mutationKey = exportModeToMutationKey[exportMode];

  const downloadMutation = useMutation<UrlToFile, unknown, ExportDownloadPayload>({
    mutationKey,
  });

  const handleSubmit = async (data: exportAnalyticsForm) => {
    const submitData: ExportDownloadPayload = {
      start_date: formatDateForApi(data.start_date),
      end_date: formatDateForApi(data.end_date),
      extension: data.extension,
      send_email_notification: data.send_email_notification,
      query,
    };

    const loading_message = `${t('inbox:export.toast_loading')} ${formatDateForApi(
      data.start_date,
    )} to ${formatDateForApi(data.end_date)}`;
    const toastId = toast.loading(loading_message, {
      duration: 600_000,
    });
    try {
      await downloadMutation.mutateAsync(submitData);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('[ExportAnalyticsForm] mutation failed', error);
      toast.dismiss(toastId);
    }
  };

  return { form, handleSubmit };
};
